import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { HammerIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { LoadingPage } from '@/components/skeletons'
import { ApiError, api } from '@/api/client'
import { Page } from '@/components/page'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { AttachmentPanel } from '@/features/attachments/AttachmentPanel'
import { BoardCard } from '@/features/assembly/BoardCard'
import { useAttachments } from '@/features/attachments/api'
import { BomEditor, draftFromEntry, type BomDraft } from '@/features/projects/BomEditor'
import { BuildWizard } from '@/features/projects/BuildWizard'
import { summarise } from '@/features/projects/buildability'
import { BomTable } from '@/features/projects/detail/BomTable'
import { BuildPlate } from '@/features/projects/detail/BuildPlate'
import { useSettings } from '@/features/settings/api'
import {
  STATUS_LABELS,
  importBom,
  projectKey,
  replaceBom,
  useProject,
  type ImportPreview,
  type ProjectDetail,
} from '@/features/projects/api'
import { formatDateTime } from '@/lib/format'
import { formatAmount } from '@/lib/quantity'

export const Route = createFileRoute('/projects/$projectId/')({ component: ProjectPage })

function ProjectPage() {
  const { projectId } = Route.useParams()
  const project = useProject(projectId)

  if (project.isPending) {
    return (
      <Page title="Project">
        <LoadingPage label="Loading the project" />
      </Page>
    )
  }

  if (project.isError || !project.data) {
    return (
      <Page title="Project">
        <Alert variant="destructive">
          <AlertTitle>Could not load this project</AlertTitle>
          <AlertDescription>
            {project.error instanceof Error ? project.error.message : 'It may have been deleted.'}
          </AlertDescription>
        </Alert>
      </Page>
    )
  }

  return <ProjectView project={project.data} />
}

/** A project (screen 3d): buildability first, then the bill of material. */
function ProjectView({ project }: { project: ProjectDetail }) {
  const queryClient = useQueryClient()
  const settings = useSettings()
  const currency = settings.data?.base_currency ?? 'EUR'
  const attachments = useAttachments('project', project.id)
  const [bom, setBom] = useState<BomDraft[]>(() => project.bom.map(draftFromEntry))
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [editing, setEditing] = useState(false)
  const [importing, setImporting] = useState(false)
  const [building, setBuilding] = useState(false)
  const [open, setOpen] = useState<'builds' | 'attachments' | null>(null)

  // A build changes stock, so the loaded bill of material goes stale.
  useEffect(() => {
    setBom(project.bom.map(draftFromEntry))
  }, [project.bom])

  const summary = useMemo(() => summarise(project.bom), [project.bom])
  const refresh = () => queryClient.invalidateQueries({ queryKey: projectKey(project.id) })

  const saveBom = useMutation({
    mutationFn: () =>
      replaceBom(
        project.id,
        bom.map((line) => ({
          part_id: line.part_id,
          name: line.name,
          quantity: line.quantity,
          mountnames: line.mountnames,
          comment: line.comment,
        })),
      ),
    onSuccess: async () => {
      setErrors({})
      setEditing(false)
      toast.success('Bill of material saved')
      await refresh()
    },
    onError: (error) => {
      if (error instanceof ApiError && error.problem.errors?.length) {
        setErrors(
          Object.fromEntries(
            error.problem.errors.map((problem) => [
              problem.field.replace(/^entries\./, ''),
              problem.message,
            ]),
          ),
        )
        toast.error('Some lines need attention')
        return
      }
      toast.error(error instanceof Error ? error.message : 'Could not save')
    },
  })

  const setStatus = useMutation({
    mutationFn: (status: string) =>
      api.PUT('/api/projects/{id}', {
        params: { path: { id: project.id } },
        body: {
          name: project.name,
          description: project.description,
          comment: project.comment,
          parent_id: project.parent_id,
          status,
        },
      }),
    onSuccess: refresh,
    onError: (error) => toast.error(error instanceof Error ? error.message : 'Could not save'),
  })

  const toggle = (section: 'builds' | 'attachments') =>
    setOpen((current) => (current === section ? null : section))

  return (
    <Page
      title={project.name}
      breadcrumb={
        <nav aria-label="Project">
          <Link to="/projects" className="hover:text-steel-700">
            Projects
          </Link>
          {project.path.slice(0, -1).map((segment) => (
            <span key={segment.id}>
              {' / '}
              <Link
                to="/projects/$projectId"
                params={{ projectId: segment.id }}
                className="hover:text-steel-700"
              >
                {segment.name}
              </Link>
            </span>
          ))}
        </nav>
      }
      description={
        <span className="flex flex-wrap items-center gap-2">
          <Badge>{STATUS_LABELS[project.status] ?? project.status}</Badge>
          {project.description && <span>{project.description}</span>}
        </span>
      }
      actions={
        <>
          <Select value={project.status} onValueChange={(status) => setStatus.mutate(status)}>
            <SelectTrigger aria-label="Status" className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="secondary"
            aria-expanded={open === 'attachments'}
            onClick={() => toggle('attachments')}
          >
            Attachments {attachments.data?.length ?? ''}
          </Button>
          <Button
            variant="secondary"
            aria-expanded={open === 'builds'}
            onClick={() => toggle('builds')}
          >
            Builds {project.builds.length}
          </Button>
          <Button onClick={() => setBuilding(true)}>
            <HammerIcon />
            Build…
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <BuildPlate summary={summary} lines={project.bom.length} currency={currency} />

        {editing ? (
          <section aria-label="Edit the bill of material" className="flex flex-col gap-4">
            <BomEditor value={bom} onChange={setBom} errors={errors} />
            <div className="flex flex-wrap gap-2">
              <Button disabled={saveBom.isPending} onClick={() => saveBom.mutate()}>
                {saveBom.isPending && <Spinner />}
                Save the bill of material
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setBom(project.bom.map(draftFromEntry))
                  setErrors({})
                  setEditing(false)
                }}
              >
                Cancel
              </Button>
            </div>
          </section>
        ) : (
          <BomTable
            bom={project.bom}
            summary={summary}
            currency={currency}
            onEdit={() => setEditing(true)}
            onImport={() => setImporting(true)}
          />
        )}

        <BoardCard projectId={project.id} />

        {open === 'builds' && (
          <section aria-label="Builds" className="flex flex-col gap-2">
            <h4 className="text-xl">Builds</h4>
            {project.builds.length === 0 ? (
              <p className="border border-dashed border-neutral-400 p-6 text-center text-sm text-neutral-600">
                Nothing has been built yet.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead className="text-right">Units</TableHead>
                    <TableHead>Note</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {project.builds.map((build) => (
                    <TableRow key={build.id}>
                      <TableCell>{formatDateTime(build.created_at)}</TableCell>
                      <TableCell className="tabular text-right">
                        {formatAmount(build.quantity)}
                      </TableCell>
                      <TableCell className="text-neutral-600">{build.comment || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </section>
        )}

        {open === 'attachments' && (
          <section aria-label="Attachments" className="flex flex-col gap-2">
            <h4 className="text-xl">Attachments</h4>
            <AttachmentPanel
              ownerType="project"
              ownerId={project.id}
              allowPreview={false}
              invalidate={[projectKey(project.id)]}
            />
          </section>
        )}
      </div>

      <ImportDialog
        projectId={project.id}
        open={importing}
        onOpenChange={setImporting}
        onDone={refresh}
      />

      <BuildWizard projectId={project.id} open={building} onOpenChange={setBuilding} />
    </Page>
  )
}

/** Upload a CSV, review what it would do, then commit. */
function ImportDialog({
  projectId,
  open,
  onOpenChange,
  onDone,
}: {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onDone: () => Promise<unknown>
}) {
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [error, setError] = useState<string | null>(null)

  const run = useMutation({
    mutationFn: ({ dryRun }: { dryRun: boolean }) => importBom(projectId, file as File, dryRun),
    onSuccess: async (result) => {
      if (result.dry_run) {
        setPreview(result)
        return
      }
      onOpenChange(false)
      setFile(null)
      setPreview(null)
      toast.success('Bill of material imported')
      await onDone()
    },
    onError: (caught) => setError(caught instanceof Error ? caught.message : 'The import failed'),
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setFile(null)
          setPreview(null)
          setError(null)
        }
        onOpenChange(next)
      }}
    >
      <DialogContent className="flex max-h-[85vh] max-w-3xl flex-col">
        <DialogHeader>
          <DialogTitle>Import a bill of material</DialogTitle>
          <DialogDescription>
            A CSV from KiCad or any other tool. Lines are matched to parts by internal part number
            first, then by manufacturer part number. Importing replaces the current bill of
            material.
          </DialogDescription>
        </DialogHeader>

        <input
          type="file"
          accept=".csv,text/csv"
          aria-label="Choose a CSV"
          onChange={(event) => {
            const chosen = event.target.files?.[0] ?? null
            event.target.value = ''
            setPreview(null)
            setError(null)
            setFile(chosen)
            if (chosen) run.mutate({ dryRun: true })
          }}
        />

        {error && <p className="text-destructive text-sm">{error}</p>}

        {run.isPending && <Spinner label="Reading the file" />}

        {preview && (
          <div className="flex min-h-0 flex-1 flex-col gap-3">
            <div className="flex gap-2">
              <Badge variant="success">{preview.matched} matched</Badge>
              {preview.unmatched > 0 && (
                <Badge variant="warning">{preview.unmatched} not in the inventory</Badge>
              )}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Row</TableHead>
                    <TableHead>Designators</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead>Matched</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.lines.map((line) => (
                    <TableRow key={line.row}>
                      <TableCell className="tabular">{line.row}</TableCell>
                      <TableCell>{line.mountnames || '—'}</TableCell>
                      <TableCell>{line.name || '—'}</TableCell>
                      <TableCell className="tabular text-right">
                        {formatAmount(line.quantity)}
                        {line.warnings.map((warning) => (
                          <div key={warning} className="text-warning text-xs">
                            {warning}
                          </div>
                        ))}
                      </TableCell>
                      <TableCell>
                        {line.part_name ? (
                          <span>
                            {line.part_name}
                            <Badge variant="muted" className="ml-2">
                              by {line.matched_by}
                            </Badge>
                          </span>
                        ) : (
                          <span className="text-muted-foreground">kept as a free-text line</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!preview || run.isPending}
            onClick={() => run.mutate({ dryRun: false })}
          >
            {run.isPending && <Spinner />}
            Replace the bill of material
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
